import React from 'react';
import { Alert, Box, CircularProgress, Stack, Typography } from '@mui/material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { fleetDashboardApi } from '../api/services';
import type { FleetDashboardPayload, FleetDevice, FleetEventEntry, FleetLocation, FleetPublishEntry } from '../api/config';
import { LocationCard, SINGLE_CARD_STRIDE_PX, STATE_RANK, pickWorstState, useSolarAlerts } from '../components/fleet-dashboard/LocationCard';
import { StagingDeviceCard } from '../components/fleet-dashboard/StagingDeviceCard';
import { CommentDrawer } from '../components/fleet-dashboard/CommentDrawer';
import { isKioskMode } from '../utils/kioskMode';

const POLL_INTERVAL_MS = 30_000;
// Health LED stale threshold — the dashboard polls every 30s, so 90s without
// a successful payload means at least 3 polls have failed in a row.
const STALE_THRESHOLD_MS = 90_000;

// Kiosk auto-scroll cadence. Tuned for the deployed FireStick player.
// Page-at-a-time stepping rather than per-pixel ticking: weak hardware paint-
// throttles subsecond setInterval callbacks down to ~1 fire / 18 s, and
// per-pixel scrollBy across that gap moves only 1 px per visible frame.
// One viewport-height jump every 30 s gives the eye time to scan and
// survives any paint cadence the player might enforce.
const KIOSK_TOP_DELAY_MS = 20_000;
const KIOSK_BOTTOM_DELAY_MS = 5_000;
const KIOSK_PAGE_INTERVAL_MS = 30_000;

// Worst observed % beacons good across a location's devices. Used as the
// within-category tie-breaker for the severity sort. Devices with no beacons
// at all (total=0) don't contribute — they have no signal yet, and their
// freshness state already routes them into pending/gray buckets.
function worstPctBeaconsGood(devices: FleetDevice[]): number {
  let worst = 1;
  for (const d of devices) {
    if (d.total_beacons === 0) continue;
    const pct = d.good_beacons / d.total_beacons;
    if (pct < worst) worst = pct;
  }
  return worst;
}

// Auto-scroll the document slowly to the bottom, then snap back to the top
// after a delay, repeat. Kiosk-only — desktop users have a mouse and resent
// the page scrolling underneath them.
function useKioskAutoScroll(enabled: boolean) {
  React.useEffect(() => {
    if (!enabled) return;

    let cancelled = false;
    let timerId: ReturnType<typeof setTimeout> | null = null;
    let intervalId: ReturnType<typeof setInterval> | null = null;

    const isAtBottom = () =>
      Math.ceil(window.scrollY + window.innerHeight) >= document.documentElement.scrollHeight;

    const startScrollingDown = () => {
      if (cancelled) return;
      intervalId = setInterval(() => {
        if (cancelled) return;
        // Step by an even multiple of the card-row stride so each page lands
        // on a clean row boundary instead of slicing through the middle of a
        // card. The -1 leaves a one-pixel sliver of the previous-bottom row
        // visible at the top of the new view as a "you just turned the page"
        // continuity marker. After scrolling, if we just landed at the bottom,
        // kick off the bottom-dwell → snap-to-top cycle. Checking post-scroll
        // (rather than pre-) avoids dwelling an extra KIOSK_PAGE_INTERVAL_MS
        // at the bottom before the snap fires.
        const rows = Math.max(1, Math.floor(window.innerHeight / SINGLE_CARD_STRIDE_PX));
        window.scrollBy(0, rows * SINGLE_CARD_STRIDE_PX - 1);
        if (isAtBottom()) {
          if (intervalId !== null) clearInterval(intervalId);
          intervalId = null;
          timerId = setTimeout(() => {
            if (cancelled) return;
            window.scrollTo(0, 0);
            timerId = setTimeout(() => {
              if (cancelled) return;
              startScrollingDown();
            }, KIOSK_TOP_DELAY_MS);
          }, KIOSK_BOTTOM_DELAY_MS);
        }
      }, KIOSK_PAGE_INTERVAL_MS);
    };

    // Initial top dwell so the first frame the kiosk shows isn't immediately
    // moving — gives the eye a moment to land before the scroll starts.
    timerId = setTimeout(startScrollingDown, KIOSK_TOP_DELAY_MS);

    return () => {
      cancelled = true;
      if (timerId !== null) clearTimeout(timerId);
      if (intervalId !== null) clearInterval(intervalId);
    };
  }, [enabled]);
}

interface DashboardState {
  payload: FleetDashboardPayload;
  /** Latest entry timestamp seen across publishes_24h + events_24h, used as
      the `since` cursor on the next poll. */
  cursor: string;
  fetchedAtMs: number;
}

// Cursor only tracks the events stream. Publishes are returned in full
// every poll (server returns the rolling 24h window unconditionally), so
// they don't need a cursor — and a unified cursor across both streams
// would silently drop publish buckets whenever events advanced past a
// not-yet-written bucket boundary.
function latestEventIso(payload: FleetDashboardPayload): string | null {
  let latest: number | null = null;
  for (const loc of payload.locations) {
    const last = loc.events_24h[loc.events_24h.length - 1];
    if (last) {
      const ms = Date.parse(last[0]);
      if (!Number.isNaN(ms) && (latest === null || ms > latest)) latest = ms;
    }
  }
  return latest === null ? null : new Date(latest).toISOString();
}

// mergeEvents drops entries outside the 24h rolling window. This is the
// safety net for re-seeds and any other server-side clock perturbation:
// stale events can't survive past the window even if the cursor races
// past the BE's new data. Publishes don't need this trim — the server
// returns the full window every poll and the client replaces wholesale.
const WINDOW_MS = 24 * 60 * 60 * 1000;

function withinWindow<T extends [string, ...unknown[]]>(entry: T, cutoffMs: number): boolean {
  const ms = Date.parse(entry[0]);
  return !Number.isNaN(ms) && ms >= cutoffMs;
}

// The server returns the full 24h window of publishes on every poll —
// always authoritative, including the empty case (a previously-active
// device that's gone silent past the window edge). Adopt `next` directly
// so the sparkline correctly flushes to all-blackout instead of
// indefinitely showing stale data.
function mergePublishes(_prev: FleetPublishEntry[], next: FleetPublishEntry[]): FleetPublishEntry[] {
  return next;
}

function mergeEvents(prev: FleetEventEntry[], next: FleetEventEntry[]): FleetEventEntry[] {
  if (next.length === 0 && prev.length === 0) return prev;
  const cutoffMs = Date.now() - WINDOW_MS;
  const combined = next.length > 0 ? [...prev, ...next] : prev;
  return combined.filter(e => withinWindow(e, cutoffMs));
}

function mergeDeviceListWithMap(
  prevById: Map<number, FleetDevice>,
  nextList: FleetDevice[]
): FleetDevice[] {
  return nextList.map(nextDev => {
    const prevDev = prevById.get(nextDev.id);
    if (!prevDev) return nextDev;
    return {
      ...nextDev,
      publishes_24h: mergePublishes(prevDev.publishes_24h, nextDev.publishes_24h),
    };
  });
}

export function mergePayloads(prev: FleetDashboardPayload, next: FleetDashboardPayload): FleetDashboardPayload {
  // Headers (last_seen_at, vin_volts, comments_count, etc.) always come back
  // fresh — overlay them. Time-series arrays are append-only.
  const prevLocationsById = new Map(prev.locations.map(l => [l.id, l]));

  // Unified prev-by-id across all three sets. When admin flips staging or
  // assigns/clears a location, a device migrates between sections — looking
  // it up only within its new section's prev list would drop the 24h publish
  // history at the moment the staging chip is supposed to show its heartbeat.
  const prevDevicesById = new Map<number, FleetDevice>();
  for (const list of [prev.devices, prev.staging_devices, prev.needs_attention_devices]) {
    for (const d of list) prevDevicesById.set(d.id, d);
  }

  const locations: FleetLocation[] = next.locations.map(nextLoc => {
    const prevLoc = prevLocationsById.get(nextLoc.id);
    if (!prevLoc) return nextLoc;
    return {
      ...nextLoc,
      events_24h: mergeEvents(prevLoc.events_24h, nextLoc.events_24h),
    };
  });

  return {
    as_of: next.as_of,
    locations,
    devices: mergeDeviceListWithMap(prevDevicesById, next.devices),
    staging_devices: mergeDeviceListWithMap(prevDevicesById, next.staging_devices),
    needs_attention_devices: mergeDeviceListWithMap(prevDevicesById, next.needs_attention_devices),
  };
}

export const FleetDashboardPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [state, setState] = React.useState<DashboardState | null>(null);
  const [drawerDeviceId, setDrawerDeviceId] = React.useState<number | null>(null);
  // Re-render every 5s for "fresh" determination + minutes-ago labels.
  const [tickMs, setTickMs] = React.useState(Date.now());
  // Cursor lives in a ref so it doesn't churn the queryKey. Putting it in
  // the key would invalidate the cache on every successful poll and trigger
  // a duplicate fetch. The ref is read inside queryFn at fetch time.
  const cursorRef = React.useRef<string | null>(null);
  const stateRef = React.useRef<DashboardState | null>(null);
  stateRef.current = state;

  React.useEffect(() => {
    const id = setInterval(() => setTickMs(Date.now()), 5_000);
    return () => clearInterval(id);
  }, []);

  const { isLoading, isError, error } = useQuery({
    queryKey: ['fleet-dashboard'],
    queryFn: async () => {
      const since = cursorRef.current ?? undefined;
      const payload = await fleetDashboardApi.get(since);
      const prevState = stateRef.current;
      const merged = prevState ? mergePayloads(prevState.payload, payload) : payload;
      // Anchor the cursor to the server's `as_of` when no events exist yet
      // — never the client wall clock. A skewed client could otherwise
      // ship a future timestamp and silently exclude real events.
      const cursor = latestEventIso(merged) ?? since ?? payload.as_of;
      cursorRef.current = cursor;
      setState({ payload: merged, cursor, fetchedAtMs: Date.now() });
      return payload;
    },
    refetchInterval: POLL_INTERVAL_MS,
    refetchIntervalInBackground: false,
    // React-query handles exponential retry on failure within a fetch; the
    // refetchInterval above keeps the dashboard self-healing on transient
    // network blips even if a single attempt exhausts retries.
    retry: 3,
    retryDelay: attempt => Math.min(2_000 * 2 ** attempt, 15_000),
  });

  // Surface the comment-count refresh on the tile after appending.
  // invalidateQueries already triggers a refetch on active observers; an
  // explicit refetch() here would queue a second fetch behind it.
  const handleCommentAppended = React.useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['fleet-dashboard'] });
  }, [queryClient]);

  const drawerDeviceLabel = React.useMemo(() => {
    if (drawerDeviceId === null || !state) return null;
    const dev = state.payload.devices.find(d => d.id === drawerDeviceId);
    if (!dev) return null;
    return dev.label || dev.coreid;
  }, [drawerDeviceId, state]);

  // Lifted from LocationCard so a single fleet-wide hysteresis pass feeds both
  // the per-card flashing animation AND the page-level severity sort. Memoize
  // the devices array so the alert effect only refires on payload change.
  // Solar-alert hysteresis only operates on operational devices — staging
  // gateways aren't in service, so a low-vin reading there isn't an alarm.
  const allDevices = React.useMemo(
    () => state?.payload.devices ?? [],
    [state],
  );
  const alertIds = useSolarAlerts(allDevices);

  // Auto-scroll only when the kiosk URL is loaded and we have data to render.
  useKioskAutoScroll(isKioskMode() && state !== null);

  if (isLoading && state === null) {
    return (
      <Stack alignItems="center" sx={{ py: 8 }}>
        <CircularProgress />
      </Stack>
    );
  }
  if (isError && state === null) {
    return (
      <Alert severity="error">
        Failed to load fleet dashboard: {(error as Error)?.message ?? 'unknown error'}
      </Alert>
    );
  }
  if (!state) return null;

  const isStale = tickMs - state.fetchedAtMs > STALE_THRESHOLD_MS;

  // Group devices by location for the LocationCard model. Each card represents
  // one site; multi-device cards span more rows in the masonry grid below.
  // Devices within a card are sorted by label (main / secondary / back / …)
  // so the visual order of the rows is meaningful at a glance. Unassigned
  // devices are filtered out at the service layer — see FleetDashboardService.
  const devicesByLocation = new Map<number, FleetDevice[]>();
  for (const dev of state.payload.devices) {
    if (dev.location_id === null) continue;
    const arr = devicesByLocation.get(dev.location_id) ?? [];
    arr.push(dev);
    devicesByLocation.set(dev.location_id, arr);
  }
  devicesByLocation.forEach((arr) => {
    arr.sort((a: FleetDevice, b: FleetDevice) =>
      (a.label ?? '').localeCompare(b.label ?? '') || a.id - b.id
    );
  });

  // Locations sorted by severity: worst-device category descending (low-vin
  // flashing > red > yellow > gray > pending > green), then within a category
  // ascending by worst % beacons good. Locations with no devices fall to the
  // end (they're filtered from the render anyway). The eye lands on the most
  // urgent sites first — works the same for both desktop and kiosk views.
  const sortedLocations = [...state.payload.locations].sort((a, b) => {
    const devsA = devicesByLocation.get(a.id) ?? [];
    const devsB = devicesByLocation.get(b.id) ?? [];
    const rankA = STATE_RANK[pickWorstState(devsA, tickMs, alertIds) ?? 'green'];
    const rankB = STATE_RANK[pickWorstState(devsB, tickMs, alertIds) ?? 'green'];
    if (rankA !== rankB) return rankB - rankA;
    return worstPctBeaconsGood(devsA) - worstPctBeaconsGood(devsB);
  });

  // Sort the staging set: unassigned first (location_id null), alphabetical by
  // label / coreid, then by location name. Dashboard is for confirming "yes,
  // it's reporting," not raising alarms — no severity sort.
  const stagingDevices = [...state.payload.staging_devices].sort((a, b) => {
    const aUnassigned = a.location_id === null;
    const bUnassigned = b.location_id === null;
    if (aUnassigned !== bUnassigned) return aUnassigned ? -1 : 1;
    if (aUnassigned) return (a.label ?? a.coreid).localeCompare(b.label ?? b.coreid);
    return (a.location?.name ?? '').localeCompare(b.location?.name ?? '');
  });
  const needsAttentionDevices = [...state.payload.needs_attention_devices].sort(
    (a, b) => (a.label ?? a.coreid).localeCompare(b.label ?? b.coreid)
  );

  return (
    <Box sx={{ p: 0 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <Box
          sx={{
            width: 10,
            height: 10,
            borderRadius: '50%',
            bgcolor: isStale ? '#c62828' : '#2e7d32',
            boxShadow: isStale ? 'none' : '0 0 6px rgba(46, 125, 50, 0.5)',
          }}
          aria-label={isStale ? 'Stale — last update over 90 s ago' : 'Live'}
        />
        <Typography variant="subtitle2" sx={{ color: isStale ? 'error.main' : 'text.secondary', fontWeight: 500 }}>
          As of {new Date(state.payload.as_of).toLocaleTimeString()}
          {isStale && ' — stale'}
        </Typography>
      </Stack>

      {(state.payload.active_platform_incidents ?? []).map((incident, i) => {
        const since = incident.started_at ? new Date(incident.started_at).toLocaleString() : 'an unknown time';
        const impact = incident.impact ? ` (${incident.impact})` : '';
        const provider = incident.provider.charAt(0).toUpperCase() + incident.provider.slice(1);
        const link = incident.url ?? 'https://status.particle.io';
        return (
          <Alert
            key={`platform-incident-${i}`}
            severity="warning"
            sx={{ mb: 2 }}
            action={
              <a href={link} target="_blank" rel="noopener noreferrer" style={{ whiteSpace: 'nowrap' }}>
                Status page
              </a>
            }
          >
            {provider} platform incident in progress since {since}: {incident.name}{impact}
          </Alert>
        );
      })}

      {needsAttentionDevices.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#b71c1c', mb: 0.5 }}>
            Needs attention — {needsAttentionDevices.length} device{needsAttentionDevices.length === 1 ? '' : 's'}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>
            No location set, not in staging — assign a location or flip to staging.
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, 272px)',
              gap: '8px',
              justifyContent: 'center',
            }}
          >
            {needsAttentionDevices.map(dev => (
              <StagingDeviceCard
                key={dev.id}
                device={dev}
                nowMs={tickMs}
                onOpenComments={setDrawerDeviceId}
              />
            ))}
          </Box>
        </Box>
      )}

      {stagingDevices.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#8d6e00', mb: 0.5 }}>
            Staging — not in service
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>
            These devices keep producing diagnostics but do not generate inventory state or beacon concerns.
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, 272px)',
              gap: '8px',
              justifyContent: 'center',
            }}
          >
            {stagingDevices.map(dev => (
              <StagingDeviceCard
                key={dev.id}
                device={dev}
                nowMs={tickMs}
                onOpenComments={setDrawerDeviceId}
              />
            ))}
          </Box>
        </Box>
      )}

      <Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1b5e20', mb: 0.5 }}>
          In service
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>
          Operational gateways at active customer locations, sorted by severity.
        </Typography>
        <Box
          sx={{
            display: 'grid',
            // auto-fit (vs auto-fill) collapses unused tracks to 0 so
            // justify-content can actually distribute the remaining space.
            // Centers the grid as a block — on the kiosk this gives symmetric
            // framing instead of flush-to-edge; on desktop it centers within
            // the main-content area.
            gridTemplateColumns: 'repeat(auto-fit, 272px)',
            gridAutoRows: '8px',
            gridAutoFlow: 'row dense',
            gap: '8px',
            justifyContent: 'center',
          }}
        >
          {sortedLocations.map(loc => {
            const devs = devicesByLocation.get(loc.id) ?? [];
            if (devs.length === 0) return null;
            return (
              <LocationCard
                key={loc.id}
                locationName={loc.name}
                devices={devs}
                events={loc.events_24h}
                nowMs={tickMs}
                alertIds={alertIds}
                onOpenComments={setDrawerDeviceId}
              />
            );
          })}
        </Box>
      </Box>

      <CommentDrawer
        deviceId={drawerDeviceId}
        deviceLabel={drawerDeviceLabel}
        onClose={() => setDrawerDeviceId(null)}
        onAppended={handleCommentAppended}
      />
    </Box>
  );
};
